import React, { useEffect, useRef, useState } from 'react';
import { CreatePurchaseContract } from '../../common/types/create-purchase-contract';
import { useAlert } from 'react-alert';
import { useErrorHandle } from '../../common/hooks/useErrorHandle';
import { useLoading } from '../../common/hooks/useLoading';
import { useApi } from '../../common/hooks/useApi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { MetodoPagamentoVendaEnum } from '../../common/types/enums/metodo-pagamento.enum';
import { useTranslation } from 'react-i18next';
import { Checkout } from '../../common/types/checkout';
import { gerarTokenRecaptcha } from '../../common/utils/getReCaptcha';
import { useDispatch } from 'react-redux';
import { OnePageCheckout } from '../pages/checkout/checkout-1-tela/checkout-1-tela.page';
import { formatCurrency } from '../../common/utils/format-currency';
import { useBackRedirect } from '../../common/hooks/useBackRedirect';
import {
  clearAllCheckouts,
  upsertCheckout,
} from '../../common/redux/slices/checkout.slice';
import { v4 as uuid } from 'uuid';
import { emitFacebookEvent } from '../../common/utils/emit-facebook-event';
import { emitTikTokEvent } from '../../common/utils/emit-tiktok-event';
import { persistTtclidFromUrl } from '../../common/utils/tiktok-utils';
import { clearEvent, upsertEvent } from '../../common/redux/slices/event.slice';
import {
  getCurrentUrlParam,
  getUrlParams,
} from '../../common/utils/get-url-params';
import { paymentStrategyMap } from '../../common/strategies/payment.strategy';
import { isRequestCanceled } from '../../common/utils/is-canceled';
import { useContentsquare } from '../../common/hooks/useContentsquare';
import { CheckoutModern } from '../pages/checkout/checkout-modern/checkout-modern.page';
import { TemplateCheckoutEnum } from '../../common/types/enums/template-checkout';
import { useApplePaySupport } from '../../common/hooks/useApplePaySupport';
import { useApplePayExpress } from '../../common/hooks/useApplePayExpress';
import { useGooglePaySupport } from '../../common/hooks/useGooglePaySupport';
import { useGooglePayExpress } from '../../common/hooks/useGooglePayExpress';
import { Sell } from '../../common/types/venda';
import { goToProcessing } from '../../common/utils/cc-utils';
import { removeMask } from '../../common/utils/remove-mask';
import { IResponsePadrao } from '../../common/types/response-padrao';
import axios from 'axios';
import { MoedaVendaEnum } from '../../common/types/enums/moeda-venda.enum';
import { usePayPalExpress } from '../../common/hooks/usePayPalExpress';
import { usePayPalSubscription } from '../../common/hooks/usePayPalSubscription';
import { StripeProvider } from '../../common/providers/stripe.provider';
import { LoadingScreen } from '../molecules/loading-screen/loading-screen.molecule';

type CheckoutRuntimeProps = {
  checkout: Checkout;
  updateCheckout: (data: Checkout) => void;
  productRouteId?: string;
};

export const HomeTemplate: React.FC = () => {
  const dispatch = useDispatch();
  const reqSeqRef = useRef(0);
  const checkoutRef = useRef<Checkout | null>(null);
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const api = useApi();
  const alert = useAlert();
  const errorHandle = useErrorHandle();
  const location = useLocation();
  const [checkout, setCheckout] = useState<Checkout | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const params = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  useContentsquare(checkout);
  useBackRedirect(checkout?.backRedirect, { extraParams: params });

  useEffect(() => {
    if (location?.state?.paymentDeclined) {
      alert.error(t('cardDeclinedMessage'), { timeout: 7000 });
      window.history.replaceState({}, document.title);
    }
  }, []);

  const updateCheckout = React.useCallback(
    (data: Checkout) => {
      const previous = checkoutRef.current;
      const next = {
        ...data,
        externalId: data.externalId ?? previous?.externalId,
        stripeAccountCountry:
          data.stripeAccountCountry ?? previous?.stripeAccountCountry,
      };

      checkoutRef.current = next;
      setCheckout(next);
      dispatch(upsertCheckout(next));
    },
    [dispatch],
  );

  const emitInitiateCheckout = React.useCallback(
    async (checkoutData: Checkout) => {
      const response = await emitFacebookEvent({
        event: 'InitiateCheckout',
        checkout: checkoutData,
      });
      if (response) {
        dispatch(
          upsertEvent({
            eventId: response?.payload?.event_id,
          }),
        );
        emitTikTokEvent({
          event: 'InitiateCheckout',
          checkout: checkoutData,
          eventId: response?.payload?.event_id,
        });
      } else {
        emitTikTokEvent({ event: 'InitiateCheckout', checkout: checkoutData });
      }
    },
    [dispatch],
  );

  useEffect(() => {
    persistTtclidFromUrl();
  }, []);

  useEffect(() => {
    if (checkout?.produtoId) {
      emitTikTokEvent({ event: 'ViewContent', checkout });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout?.produtoId]);

  useEffect(() => {
    if (!id) return;

    const ac = new AbortController();
    const mySeq = ++reqSeqRef.current;

    checkoutRef.current = null;
    setCheckout(null);

    (async () => {
      try {
        setIsLoading(true);

        const afiliateId = getUrlParams('affiliate');
        const country = getCurrentUrlParam('country');

        const resp = await api.Produto.getForCheckout(
          id,
          afiliateId ?? undefined,
          { signal: ac.signal, country: country ?? undefined },
        );

        if (mySeq !== reqSeqRef.current) return;

        const checkoutData = resp.data;
        const data = { ...checkoutData, externalId: uuid() };

        await i18n.changeLanguage(data?.linguagem);

        if (mySeq !== reqSeqRef.current) return;

        updateCheckout(data);
      } catch (e: any) {
        const isCanceled =
          e?.name === 'AbortError' ||
          e?.name === 'CanceledError' ||
          e?.code === 'ERR_CANCELED' ||
          axios.isCancel?.(e);

        if (isCanceled) return;

        if (mySeq !== reqSeqRef.current) return;

        errorHandle(e);
      } finally {
        if (mySeq === reqSeqRef.current) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      ac.abort();
    };
  }, [id]);

  useEffect(() => {
    if (checkout?.produtoId) {
      emitInitiateCheckout(checkout);
    }
  }, [checkout?.id]);

  if (!checkout?.id || isLoading) {
    return <LoadingScreen />;
  }

  return (
    <StripeProvider checkout={checkout}>
      <CheckoutRuntime
        checkout={checkout}
        updateCheckout={updateCheckout}
        productRouteId={id}
      />
    </StripeProvider>
  );
};

const CheckoutRuntime: React.FC<CheckoutRuntimeProps> = ({
  checkout,
  updateCheckout,
  productRouteId,
}) => {
  const { supportsApplePay } = useApplePaySupport();
  const { supportsGooglePay } = useGooglePaySupport();
  const dispatch = useDispatch();
  const id = productRouteId;
  const { t } = useTranslation();
  const api = useApi();
  const alert = useAlert();
  const errorHandle = useErrorHandle();
  const loading = useLoading();
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentMethod, setPaymentMethod] =
    React.useState<MetodoPagamentoVendaEnum>(
      MetodoPagamentoVendaEnum.CARTAO_CREDITO,
    );
  const [couponMessage, setCouponMessage] = useState('');
  const [discount, setDiscount] = useState(0);
  const [isOpeningAmazonPay, setIsOpeningAmazonPay] = useState(false);
  const [cupomPercentual, setCupomPercentual] = useState(0);
  const [optimisticBumps, setOptimisticBumps] = useState<
    Record<string, boolean>
  >({});
  const isLoading = false;

  const abortMapRef = React.useRef<Record<string, AbortController>>({});
  const timersRef = React.useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );
  const desiredRef = React.useRef<Record<string, boolean>>({});
  const nonceRef = React.useRef<Record<string, number>>({});

  const subtotalAtual = React.useMemo(() => {
    const baseProduto = checkout?.total ?? 0;

    let orderBumpsSum = 0;
    if (Object.keys(optimisticBumps).length > 0) {
      checkout?.orderBumps?.forEach((ob) => {
        if (optimisticBumps[ob.id]) {
          orderBumpsSum += ob?.precos?.[0]?.valor ?? 0;
        }
      });
    } else {
      orderBumpsSum = (checkout?.orderBumpsAplicados ?? []).reduce(
        (acc, ob) => acc + (ob?.precos?.[0]?.valor ?? 0),
        0,
      );
    }

    return baseProduto + orderBumpsSum;
  }, [
    checkout?.total,
    checkout?.orderBumps,
    checkout?.orderBumpsAplicados,
    optimisticBumps,
  ]);

  const userPickedMethodRef = useRef(false);

  const setPaymentMethodUser = (m: MetodoPagamentoVendaEnum) => {
    userPickedMethodRef.current = true;
    setPaymentMethod(m);
  };

  const hasMethod = (checkout: Checkout | null | undefined, key: string) =>
    (checkout?.metodosPagamento ?? '').split(',').includes(key);

  const isAndroidDevice = () => {
    if (typeof navigator === 'undefined') return false;
    return /Android/i.test(navigator.userAgent);
  };

  const pickDefaultMethod = (checkout: Checkout) => {
    const isBRL = checkout?.moeda === MoedaVendaEnum.BRL;

    const total = checkout?.total ?? checkout?.valor ?? 0;

    const LOW_TICKET_MAX_BRL = 99;

    if (isBRL) {
      const pixEnabled = hasMethod(checkout, 'pix');
      const ccEnabled = hasMethod(checkout, 'cc');

      if (total > 0 && total <= LOW_TICKET_MAX_BRL && pixEnabled)
        return MetodoPagamentoVendaEnum.PIX;

      if (ccEnabled) return MetodoPagamentoVendaEnum.CARTAO_CREDITO;
      if (pixEnabled) return MetodoPagamentoVendaEnum.PIX;

      return MetodoPagamentoVendaEnum.CARTAO_CREDITO;
    }

    // Moeda estrangeira: preferir wallet do device
    const appleEnabled = hasMethod(checkout, 'applepay');
    const googleEnabled = hasMethod(checkout, 'googlepay');
    const ccEnabled = hasMethod(checkout, 'cc');

    // iPhone: Apple Pay (quando disponível)
    if (applePayAvailable && appleEnabled) return MetodoPagamentoVendaEnum.APPLE_PAY;

    // Android: Google Pay (quando disponível)
    if (isAndroidDevice() && googlePayAvailable && googleEnabled)
      return MetodoPagamentoVendaEnum.GOOGLE_PAY;

    // fallback
    if (googlePayAvailable && googleEnabled) return MetodoPagamentoVendaEnum.GOOGLE_PAY;
    if (ccEnabled) return MetodoPagamentoVendaEnum.CARTAO_CREDITO;

    return MetodoPagamentoVendaEnum.CARTAO_CREDITO;
  };

  useEffect(() => {
    if (cupomPercentual > 0 && subtotalAtual > 0) {
      const discountValue = subtotalAtual * (cupomPercentual / 100);
      setDiscount(discountValue);
    } else {
      setDiscount(0);
    }
  }, [subtotalAtual, cupomPercentual]);

  const handleApplePaySuccess = (responsePadrao: IResponsePadrao<Sell>) => {
    const { data: venda } = responsePadrao;
    const safeContract = {
      emailComprador: venda.emailComprador,
      nomeComprador: venda.nomeComprador,
      sobrenomeComprador: venda.nomeComprador,
      cpfComprador: removeMask(venda.cpfComprador),
      telefoneComprador: removeMask(venda.telefoneComprador),
      postalCode: venda?.postalCode,
      state: venda?.state,
      city: venda?.city,
      district: venda?.district,
      address: venda?.address,
      number: venda?.number,
      complement: venda?.complement,
    };
    goToProcessing(
      navigate,
      location.search,
      responsePadrao,
      checkout,
      safeContract,
    );
    dispatch(clearEvent());
    dispatch(clearAllCheckouts());
  };

  const { applePayAvailable, handleApplePayClick, isOpeningApplePay } =
    useApplePayExpress({
      checkout,
      api,
      gerarTokenRecaptcha,
      onSuccess: handleApplePaySuccess,
      supportsApplePay,
    });

  const handleGooglePaySuccess = (responsePadrao: IResponsePadrao<Sell>) => {
    const { data: venda } = responsePadrao;
    const safeContract = {
      emailComprador: venda.emailComprador,
      nomeComprador: venda.nomeComprador,
      sobrenomeComprador: venda.nomeComprador,
      cpfComprador: removeMask(venda.cpfComprador),
      telefoneComprador: removeMask(venda.telefoneComprador),
      postalCode: venda?.postalCode,
      state: venda?.state,
      city: venda?.city,
      district: venda?.district,
      address: venda?.address,
      number: venda?.number,
      complement: venda?.complement,
    };
    goToProcessing(
      navigate,
      location.search,
      responsePadrao,
      checkout,
      safeContract,
    );
    dispatch(clearEvent());
    dispatch(clearAllCheckouts());
  };

  const { googlePayAvailable, handleGooglePayClick, isOpeningGooglePay } =
    useGooglePayExpress({
      checkout,
      api,
      gerarTokenRecaptcha,
      onSuccess: handleGooglePaySuccess,
      supportsGooglePay,
    });

  const handlePayPalExpressSuccess = (vendaId: string, vendaToken: string) => {
    const responsePadrao: IResponsePadrao<Sell> = {
      error: false,
      message: ['Pagamento PayPal aprovado'],
      data: {
        id: vendaId,
        vendaToken,
        produtoId: checkout?.produtoId,
        valorPago: checkout?.subtotal,
        moeda: checkout?.moeda,
        metodoPagamento: MetodoPagamentoVendaEnum.PAY_PAL,
        produto: checkout?.produto,
      } as Sell,
    };
    goToProcessing(navigate, location.search, responsePadrao, checkout, {});
    dispatch(clearEvent());
    dispatch(clearAllCheckouts());
  };

  const {
    paypalAvailable,
    paypalLoading,
    paypalCurrency,
    createOrder: paypalCreateOrder,
    onApprove: paypalOnApprove,
    onCancel: paypalOnCancel,
  } = usePayPalExpress({
    checkout,
    api,
    gerarTokenRecaptcha,
    onSuccess: handlePayPalExpressSuccess,
    onError: (msg) => alert.show(msg),
  });

  const {
    paypalSubscriptionAvailable,
    paypalSubscriptionLoading,
    paypalSubscriptionCurrency,
    createSubscription: paypalCreateSubscription,
    onApprove: paypalSubOnApprove,
    onCancel: paypalSubOnCancel,
  } = usePayPalSubscription({
    checkout,
    api,
    gerarTokenRecaptcha,
    onSuccess: handlePayPalExpressSuccess,
    onError: (msg) => alert.show(msg),
  });

  const isPaypalSubscription = !!checkout?.recorrencia;
  const mergedPaypal = isPaypalSubscription
    ? {
        available: paypalSubscriptionAvailable,
        loading: paypalSubscriptionLoading,
        currency: paypalSubscriptionCurrency,
        createSubscription: paypalCreateSubscription,
        onApprove: (data: { orderID: string; subscriptionID?: string }) =>
          paypalSubOnApprove({ subscriptionID: data.subscriptionID ?? data.orderID }),
        onCancel: paypalSubOnCancel,
        isSubscription: true as const,
      }
    : {
        available: paypalAvailable,
        loading: paypalLoading,
        currency: paypalCurrency,
        createOrder: paypalCreateOrder,
        onApprove: paypalOnApprove,
        onCancel: paypalOnCancel,
        isSubscription: false as const,
      };

  const onSubmit = async (data: CreatePurchaseContract) => {
    try {
      loading.start();
      emitFacebookEvent({ event: 'AddPaymentInfo', checkout, data });
      emitTikTokEvent({ event: 'AddPaymentInfo', checkout, data });

      const strategy = paymentStrategyMap[paymentMethod];
      if (!strategy) throw new Error('Método de pagamento não suportado.');

      await strategy({
        data,
        checkout,
        paymentMethod,
        id,
        api,
        alert,
        navigate,
        location,
        dispatch,
        gerarTokenRecaptcha,
        getUrlParams,
      });

      dispatch(clearEvent());
      dispatch(clearAllCheckouts());
    } catch (e: any) {
      errorHandle(e);
    } finally {
      loading.stop();
    }
  };

  const onAmazonPayExpress = async () => {
    try {
      setIsOpeningAmazonPay(true);
      loading.start();

      const strategy = paymentStrategyMap[MetodoPagamentoVendaEnum.AMAZON_PAY];
      if (!strategy) throw new Error('Método de pagamento não suportado.');

      await strategy({
        data: { card: null } as CreatePurchaseContract,
        checkout,
        paymentMethod: MetodoPagamentoVendaEnum.AMAZON_PAY,
        id,
        api,
        alert,
        navigate,
        location,
        dispatch,
        gerarTokenRecaptcha,
        getUrlParams,
      });
    } catch (e: any) {
      errorHandle(e);
    } finally {
      loading.stop();
      setIsOpeningAmazonPay(false);
    }
  };

  const onApplyCoupon = async (couponCode: string) => {
    try {
      if (!couponCode) {
        setCouponMessage(t('pleaseEnterCoupon'));
        return;
      }
      const token = await gerarTokenRecaptcha('/venda/cupom_desconto');
      const res = await api.Venda.aplicarCupom(couponCode, checkout?.id, token);
      const descontoPercentual = res?.data?.cupom?.desconto ?? 0;

      const discountValue = subtotalAtual * (descontoPercentual / 100);

      setCupomPercentual(descontoPercentual);
      setDiscount(discountValue);
      setCouponMessage(
        t('couponApplied', {
          desconto: `${formatCurrency(discountValue, checkout?.moeda)}`,
        }),
      );
      updateCheckout(res.data);
    } catch (e) {
      setCouponMessage(t('invalidCoupon'));
      setCupomPercentual(0);
      setDiscount(0);
    }
  };
  const onAcceptOrderBump = (applied: boolean, id: string) => {
    desiredRef.current[id] = applied;

    setOptimisticBumps((prev) => ({ ...prev, [id]: applied }));

    if (timersRef.current[id]) clearTimeout(timersRef.current[id]);
    timersRef.current[id] = setTimeout(async () => {
      try {
        abortMapRef.current[id]?.abort();
        const ac = new AbortController();
        abortMapRef.current[id] = ac;

        const nonce = (nonceRef.current[id] ?? 0) + 1;
        nonceRef.current[id] = nonce;

        const token = await gerarTokenRecaptcha('/venda/order_bump');

        const response = await api.Venda.aplicarOrderBump(
          {
            orderBumpId: id,
            checkoutId: checkout?.id,
            applied: desiredRef.current[id],
          },
          token,
          { signal: ac.signal },
        );

        if (nonceRef.current[id] !== nonce) return;

        setOptimisticBumps((prev) => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });
        updateCheckout(response.data);
      } catch (e: any) {
        if (isRequestCanceled(e)) return;
        setOptimisticBumps((prev) => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });
        errorHandle(e);
      }
    }, 200);
  };
  const didAutoPickRef = useRef(false);

  useEffect(() => {
    if (!checkout?.id) return;
    if (didAutoPickRef.current) return;
    if (userPickedMethodRef.current) return;

    // Espera wallets ficarem “estáveis” (pelo menos uma vez)
    // Se você tiver "checked" no hook, melhor ainda.
    // Aqui vamos só aceitar: quando tiver checkout.id, decide.
    const recommended = pickDefaultMethod(checkout);

    setPaymentMethod(recommended);
    didAutoPickRef.current = true;
  }, [checkout?.id, applePayAvailable, googlePayAvailable]);

  if (checkout?.bloqueadoParaVenda) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: '#f7f7f8',
        }}
      >
        <main
          style={{
            width: '100%',
            maxWidth: 520,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: 28,
            textAlign: 'center',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
          }}
        >
          {checkout?.produto?.imagemProduto && (
            <img
              src={checkout.produto.imagemProduto}
              alt={checkout?.produto?.nome ?? 'Produto'}
              style={{
                width: 96,
                height: 96,
                objectFit: 'cover',
                borderRadius: 8,
                marginBottom: 18,
              }}
            />
          )}
          <h1 style={{ fontSize: 22, margin: '0 0 10px', color: '#111827' }}>
            Produto indisponível
          </h1>
          <p style={{ margin: 0, color: '#4b5563', lineHeight: 1.5 }}>
            {checkout.motivoBloqueioVenda ??
              'Este produto está temporariamente indisponível para compra. Entre em contato com o vendedor.'}
          </p>
        </main>
      </div>
    );
  }

  return checkout?.checkoutConfig?.config?.checkoutData?.template ===
    TemplateCheckoutEnum.MINIMALISTA ||
    !checkout?.checkoutConfig?.config?.checkoutData?.template ? (
    <CheckoutModern
      applePay={{
        available: applePayAvailable,
        onClick: handleApplePayClick,
        loading: isOpeningApplePay,
      }}
      googlePay={{
        available: googlePayAvailable,
        onClick: handleGooglePayClick,
        loading: isOpeningGooglePay,
      }}
      amazonPay={{
        available: checkout?.metodosPagamento?.includes('amazonpay') ?? false,
        onClick: onAmazonPayExpress,
        loading: isOpeningAmazonPay,
      }}
      paypal={mergedPaypal}
      checkout={checkout}
      paymentMethod={paymentMethod}
      setPaymentMethod={setPaymentMethodUser}
      onSubmit={onSubmit}
      onToggleOrderBump={onAcceptOrderBump}
      isLoading={isLoading}
      onApplyCoupon={onApplyCoupon}
      couponMessage={couponMessage}
      discount={discount}
    />
  ) : (
    <OnePageCheckout
      applePay={{
        available: applePayAvailable,
        onClick: handleApplePayClick,
        loading: isOpeningApplePay,
      }}
      googlePay={{
        available: googlePayAvailable,
        onClick: handleGooglePayClick,
        loading: isOpeningGooglePay,
      }}
      amazonPay={{
        available: checkout?.metodosPagamento?.includes('amazonpay') ?? false,
        onClick: onAmazonPayExpress,
        loading: isOpeningAmazonPay,
      }}
      paypal={mergedPaypal}
      checkout={checkout}
      paymentMethod={paymentMethod}
      setPaymentMethod={setPaymentMethodUser}
      onSubmit={onSubmit}
      onToggleOrderBump={onAcceptOrderBump}
      isLoading={isLoading}
      onApplyCoupon={onApplyCoupon}
      couponMessage={couponMessage}
      discount={discount}
    />
  );
};
